import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight } from 'lucide-react';
import { useAdEvent } from '../../hooks/useAdEvent';
import { AdResponse, AdPosition } from '../../types/Ad.api.type';
import { getAdIconPreset } from '../../constants/adIcons';

interface AdCardProps {
  ad: AdResponse;
  position: AdPosition;
}

/**
 * 피드 광고 카드 (CompactAdCard - 모바일/PC 공용)
 * 설문 카드 사이에 삽입되는 가로형 광고
 */
const AdCard = ({ ad, position }: AdCardProps) => {
  /** Hooks */
  const navigate = useNavigate();
  const { impressionRef, handleClick } = useAdEvent(ad.adId, position);
  const preset = getAdIconPreset(ad.iconType);
  const IconComp = preset.icon;

  /** Handlers */
  const handleCardClick = () => {
    handleClick();
    if (ad.adType === 'SURVEY' && ad.targetQuestionId) {
      navigate(`/question/info/${ad.targetQuestionId}`);
    } else if (ad.adType === 'EXTERNAL' && ad.targetUrl) {
      window.open(ad.targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div ref={impressionRef}>
      <Card
        className="cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 border-dashed border-blue-200 bg-blue-50/30"
        onClick={handleCardClick}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            {/* 로고/아이콘 */}
            {ad.iconType === 'custom' && ad.brandLogoUrl ? (
              <img src={ad.brandLogoUrl} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: preset.bgColor }}
              >
                <IconComp size={16} color={preset.iconColor} />
              </div>
            )}

            {/* 텍스트 */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-300 text-blue-600 font-medium">
                  광고
                </Badge>
                {ad.brandName && (
                  <span className="text-[11px] text-gray-400">{ad.brandName}</span>
                )}
              </div>
              <p className="text-sm font-medium text-gray-800 truncate">{ad.title}</p>
            </div>

            {/* 화살표 */}
            <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdCard;
